import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  //   Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  CardContent,
  TablePagination,
  SelectChangeEvent,
} from "@mui/material";
import { Grid } from "@material-ui/core";
import { useNavigate } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import adminServices from "../../services/adminServices";
import { getAdminDashboardData } from "../../types/adminSection";
import checkBroken from "../../assets/images/adminSection/check-broken.svg";
import clockForward from "../../assets/images/adminSection/clock-forward.svg";
import fileAnalytics from "../../assets/images/adminSection/file-analytics.svg";
import circleContained from "../../assets/images/adminSection/x-circle-contained.svg";
import filter from "../../assets/images/adminSection/filter.svg";
import download from "../../assets/images/adminSection/download-02.svg";
import CustomDatePicker from "../../components/common/customDatePicker";
import CustomSelect from "../../components/common/customSelect";
import dayjs from "dayjs";
import {
  AwardTypeEnum,
  CategoryType,
  RRStatusEnum,
} from "../../helper/typeEnum";
import AutoCompleteUserSelect, {
  UserOption,
} from "../../components/AutoCompleteUserSelect/AutoCompleteUserSelect";
import { showToast } from "../../components/common/toastNotification";
import RewardAcceptanceForm from "../successPopUp/rewardAcceptanceForm";
import { RewardsRecognitionDataData } from "../../types/dashboard";
import dashboardService from "../../services/DashboardServices";
import { useAppSelector } from "../../globalState/hooks";

interface CardItem {
  label: string;
  count: number;
  icon: string;
}

export interface RewardNominationMember {
  nominationId: number;
  userId: string;
  employeeName: string;
  photoUrl: string;
}

export interface RewardRow {
  id: number;
  awardType: string;
  categoryType: string;
  status: string;
  statusName: string;
  nominatedBy: string;
  nominatedByName: string;
  lstRewardAndRecognizationNomination_Members: RewardNominationMember[];
  crossFunctionalType?: string;
}

const getStatusColor = (status: string) => {
  switch (String(status)) {
    case "1":
      return "orange";
    case "2":
      return "#34C759";
    case "3":
      return "#FF3B30";
    default:
      return "gray";
  }
};

const RandRDashboard = () => {
  const navigate = useNavigate();
  const [cardCount, setCardCount] = useState<CardItem[]>([]);
  const [tableData, setTableData] = useState<RewardRow[]>([]);
  const [rewardTypeData, setRewardTypeData] = useState<
    { label: string; value: number | string }[]
  >([]);
  const [selectedRewardType, setSelectedRewardType] = useState(0);
  const [selectedUser, setSelectedUser] = useState<UserOption | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [formData, setFormData] = useState<getAdminDashboardData>({
    startDate: "",
    endDate: "",
    status: "",
    categoryType: "",
    recommendedBy: "",
    categoryTypeId: 0,
    awardId: 0,
  });
  const [formErrors, setFormErrors] = useState<
    Partial<getAdminDashboardData & { general?: string }>
  >({});
  const [typeData, setTypeData] = useState<
    { label: string; value: number | string }[]
  >([]);
  const [statusData, setStatusData] = useState<
    { label: string; value: number | string }[]
  >([]);
  const [isRewardAcceptanceFormOpen, setIsRewardAcceptanceFormOpen] =
    useState(true);
  const [rewardAcceptanceFormData, setRewardAcceptanceFormData] =
    useState<RewardsRecognitionDataData | null>(null);
  const financialYear = useAppSelector(
    (state) =>
      state.dashboardReducer?.financialYear || { startDate: "", endDate: "" }
  );
  // get table list function
  const fetchTableData = useCallback(
    async (filters?: getAdminDashboardData) => {
      const input: getAdminDashboardData = {
        pageNo: page + 1,
        pageSize,
        startDate: filters?.startDate || financialYear.startDate,
        endDate: filters?.endDate || financialYear.endDate,
        status: filters?.status ?? null,
        awardId: filters?.awardId ?? null,
        categoryId: filters?.categoryId ?? null,
        nominatedBy: filters?.nominatedBy ?? null,
      };

      try {
        const response = await adminServices.getRandRADashboardData(input);
        setTableData(response.data.data.data);
      } catch (error) {
        console.error("Failed to fetch table data", error);
        setTableData([]);
      }
    },
    [page, pageSize]
  );
  useEffect(() => {
    fetchCardData();
    fetchTableData();
    const tempRRAwardType = Object.entries(AwardTypeEnum).map(
      ([key, value]) => ({
        label: key,
        value: value,
      })
    );

    setRewardTypeData(tempRRAwardType);

    const tempType = Object.entries(CategoryType).map(([key, value]) => ({
      label: key,
      value: value,
    }));

    setTypeData(tempType);

    const tempStatusType = Object.entries(RRStatusEnum).map(([key, value]) => ({
      label: key,
      value: value,
    }));

    setStatusData(tempStatusType);
  }, [fetchTableData, page, pageSize]);

  const handleChange = (field: keyof getAdminDashboardData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  //   search filter apply
  const handleSearch = () => {
    const errors: typeof formErrors = {};

    if (formData.startDate && formData.endDate) {
      if (dayjs(formData.startDate).isAfter(dayjs(formData.endDate))) {
        errors.startDate = "Start date cannot be after end date";
        errors.endDate = "End date must be after start date";
        showToast("Start date cannot be after end date", "error");
      }
    }

    const hasAnyFilter =
      formData.startDate ||
      formData.endDate ||
      formData.status ||
      formData.categoryTypeId ||
      selectedUser?.id ||
      formData.rewardType ||
      formData.type;

    if (!hasAnyFilter) {
      errors.general = "Please select at least one filter to search.";
      showToast("Please select at least one filter to search.", "error");
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setFormErrors({});
    const payload: getAdminDashboardData = {
      pageNo: page + 1,
      pageSize: pageSize,
      startDate: formData.startDate || financialYear.startDate,
      endDate: formData.endDate || financialYear.endDate,
      status: formData.status !== "" ? formData.status : null,
      awardId: selectedRewardType !== 0 ? Number(selectedRewardType) : null,
      categoryId:
        formData.categoryTypeId !== 0 ? Number(formData.categoryTypeId) : null,
      nominatedBy: selectedUser?.id || null,
    };

    fetchTableData(payload);
  };

  const handleTypeDropDownChange = (
    event: SelectChangeEvent<string | number>
  ) => {
    const { value, name } = event.target;
    if (name === "rewardType") {
      const newRewardType = Number(value);
      setSelectedRewardType(newRewardType);
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setFormErrors((prevErrors) => ({
      ...prevErrors,
      categoryType: "",
      awardType: "",
      crossFunctionalType: "",
      ...(name === "categoryTypeId" && { userIds: "" }),
    }));
  };
  //   get card data
  const fetchCardData = async () => {
    const input: getAdminDashboardData = {
      startDate: financialYear.startDate,
      endDate: financialYear.endDate,
    };

    try {
      const response = await adminServices.getAdminDashboardCard(input);
      const apiData = response.data.data;
      const CardData: CardItem[] = [
        { label: "All", count: apiData.totaRecord || 0, icon: fileAnalytics },
        {
          label: "Pending",
          count: apiData.totalPending || 0,
          icon: clockForward,
        },
        {
          label: "Rejected",
          count: apiData.totalRejected || 0,
          icon: circleContained,
        },
        {
          label: "Approved",
          count: apiData.totalApproved || 0,
          icon: checkBroken,
        },
      ];
      setCardCount(CardData);
      setTotalCount(apiData.totaRecord || 0);
    } catch (error) {
      console.error("Failed to fetch dashboard summary", error);
      setCardCount([]);
    }
  };

  //   download excel function

  const handleExportExcel = async () => {
    const exportPayload: getAdminDashboardData = {
      startDate: formData.startDate || financialYear.startDate,
      endDate: formData.endDate || financialYear.endDate,
      status: formData.status !== "" ? formData.status : null,
      awardId: selectedRewardType !== 0 ? selectedRewardType : null,
      categoryId:
        formData.categoryTypeId !== 0 ? Number(formData.categoryTypeId) : null,
      nominatedBy: selectedUser?.id || null,
    };

    try {
      const response = await adminServices.RRDashboardExport(exportPayload);
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const fileURL = window.URL.createObjectURL(blob);
      const fileLink = document.createElement("a");

      fileLink.href = fileURL;
      fileLink.setAttribute("download", "RR_Dashboard_Export.xlsx");

      document.body.appendChild(fileLink);
      fileLink.click();
      document.body.removeChild(fileLink);
      showToast("Excel file exported successfully.", "success");
    } catch (error) {
      console.error("Excel export failed.", error);
    }
  };

  const fetchRewardAndRecognitionStatus = async (id: number) => {
    setIsRewardAcceptanceFormOpen(true);
    navigate(`/RandRDashboard?RewardAndRecognitionId=${id}`, { replace: true });

    try {
      const response = await dashboardService.GetRewardAndRecognitionStatusAPI(
        id
      );
      const finalResponse = response.data;
      if (finalResponse.success) {
        setRewardAcceptanceFormData(finalResponse.data);
      }
    } catch (error) {
      console.error("Error fetching reward status:", error);
    }
  };

  return (
    <>
      <Box p={3}>
        {/* Card Summary Section */}
        <Grid container spacing={2} style={{ marginBottom: "16px" }}>
          {cardCount.map((item) => (
            <Grid item xs={6} md={3} key={item.label}>
              <Card
                sx={{
                  backgroundColor: "#0A0A0A",
                  color: "#fff",
                  p: "16px",
                  position: "relative",
                  minHeight: 100,
                }}
              >
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="space-between"
                  mt={2}
                >
                  <Typography className="f-16" sx={{ mb: 1 }}>
                    {item.label}
                  </Typography>
                  <Typography variant="h4">{item.count}</Typography>
                </Box>
                <Box sx={{ position: "absolute", top: 40, right: 16 }}>
                  <img src={item.icon} alt="img" />
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Table and Filter/Download Buttons */}
        <Card
          sx={{
            backgroundColor: "#212121",
            mb: 2,
            borderRadius: 0,
            boxShadow: "none",
          }}
        >
          <CardContent>
            <Grid
              container
              style={{
                marginBottom: 8,
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography sx={{ color: "#fff", fontWeight: "bold" }}>
                Rewards and Recognition
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Button
                  className="filterBtn f-14"
                  style={{
                    minWidth: 140,
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "0 15px",
                  }}
                  onClick={() => setShowFilters((prev) => !prev)}
                >
                  <Typography>Filter</Typography>
                  <img src={filter} alt="filter" />
                </Button>

                <Button
                  className="downloadBtn f-14"
                  variant="contained"
                  style={{
                    minWidth: 140,
                    display: "flex",
                    justifyContent: "space-between",
                    marginLeft: 20,
                  }}
                  onClick={handleExportExcel}
                >
                  <Typography>Download</Typography>
                  <Box>
                    <img
                      src={download}
                      width={20}
                      style={{ marginTop: 7 }}
                      alt="download"
                    />
                  </Box>
                </Button>
              </Box>
            </Grid>
            {showFilters && (
              <Box
                sx={{
                  backgroundColor: "#5D5D5D",
                  padding: 2,
                  borderRadius: "4px",
                  marginTop: 2,
                }}
              >
                <Grid
                  container
                  spacing={2}
                  style={{ backgroundColor: "#5D5D5D", padding: 2 }}
                >
                  <Grid item xs={12} md={3}>
                    <CustomDatePicker
                      name="startDate"
                      label="From Date"
                      backgroundColor="#5D5D5D"
                      placeholder="Select"
                      value={
                        formData.startDate ? dayjs(formData.startDate) : null
                      }
                      shouldDisableDate={(date) => date.isSame(dayjs())}
                      onChange={(date) =>
                        handleChange(
                          "startDate",
                          date ? date.format("YYYY-MM-DD") : ""
                        )
                      }
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <CustomDatePicker
                      name="endDate"
                      label="To Date"
                      backgroundColor="#5D5D5D"
                      shouldDisableDate={(date) => date.isSame(dayjs())}
                      placeholder="Select"
                      value={formData.endDate ? dayjs(formData.endDate) : null}
                      onChange={(date) =>
                        handleChange(
                          "endDate",
                          date ? date.format("YYYY-MM-DD") : ""
                        )
                      }
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <AutoCompleteUserSelect
                      label="Recommended By"
                      placeholder="Recommended By"
                      value={selectedUser}
                      backgroundColor="#5D5D5D"
                      onChange={(user) => {
                        setSelectedUser(user);
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <CustomSelect
                      name="rewardType"
                      label="Reward Type"
                      backgroundColor="#5D5D5D"
                      placeHolder="Select Type"
                      value={selectedRewardType}
                      onChange={handleTypeDropDownChange}
                      options={rewardTypeData}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <CustomSelect
                      name="categoryTypeId"
                      label="Type (individual / Team)"
                      backgroundColor="#5D5D5D"
                      placeHolder="Select Type"
                      value={formData.categoryTypeId || ""}
                      onChange={handleTypeDropDownChange}
                      options={typeData}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <CustomSelect
                      name="status"
                      backgroundColor="#5D5D5D"
                      label="Status"
                      placeHolder="Select Status"
                      value={formData.status ?? ""}
                      onChange={handleTypeDropDownChange}
                      options={statusData}
                    />
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    md={6}
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                    }}
                  >
                    <Button
                      className="otherButton f-14"
                      variant="contained"
                      style={{
                        borderRadius: 0,
                        backgroundColor: "#5D5D5D",
                        width: 140,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginLeft: 20,
                      }}
                      onClick={handleSearch}
                    >
                      Search
                    </Button>
                    <Button
                      className="otherButton f-14"
                      variant="contained"
                      style={{
                        borderRadius: 0,
                        backgroundColor: "#5D5D5D",
                        width: 140,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginLeft: 20,
                      }}
                      onClick={() => {
                        setFormData({
                          startDate: "",
                          endDate: "",
                          status: "",
                          categoryType: "",
                          recommendedBy: "",
                          categoryTypeId: 0,
                          awardId: 0,
                        });
                        fetchTableData();
                        setFormData({});
                        setSelectedUser(null);
                        setSelectedRewardType(0);
                        setFormErrors({});
                      }}
                    >
                      Clear
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Table Section */}
            <TableContainer
              sx={{
                backgroundColor: "#212121",
                padding: 0,
                minHeight: "300px",
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    {[
                      "Recommended By",
                      "Reward Type",
                      "ECode",
                      "Nominated Employee",
                      "Type (Individual / Team)",
                      "Status",
                      "Action",
                    ].map((header) => (
                      <TableCell
                        key={header}
                        sx={{
                          color: "#fff",
                          background: "#0A0A0A",
                          fontWeight: "600",
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableData.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        sx={{
                          color: "#fff",
                          textAlign: "center",
                          alignItems: "center",
                        }}
                      >
                        No data available.
                      </TableCell>
                    </TableRow>
                  ) : (
                    tableData.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ color: "#fff" }}>
                          {row.nominatedByName} {row.nominatedBy}
                        </TableCell>
                        <TableCell sx={{ color: "#fff" }}>
                          {row.awardType}
                        </TableCell>
                        <TableCell
                          sx={{ color: "#fff" }}
                          title={row.lstRewardAndRecognizationNomination_Members
                            .slice(1)
                            .map((m) => m.userId)
                            .join(", ")}
                        >
                          {
                            row.lstRewardAndRecognizationNomination_Members[0]
                              .userId
                          }
                          {row.lstRewardAndRecognizationNomination_Members
                            .length > 1 && (
                            <>
                              {" "}
                              +{" "}
                              {row.lstRewardAndRecognizationNomination_Members
                                .length - 1}
                            </>
                          )}
                        </TableCell>
                        <TableCell
                          sx={{ color: "#fff" }}
                          title={row.lstRewardAndRecognizationNomination_Members
                            .slice(1)
                            .map((m) => m.employeeName)
                            .join(", ")}
                        >
                          {
                            row.lstRewardAndRecognizationNomination_Members[0]
                              .employeeName
                          }
                          {row.lstRewardAndRecognizationNomination_Members
                            .length > 1 && (
                            <>
                              {" "}
                              +{" "}
                              {row.lstRewardAndRecognizationNomination_Members
                                .length - 1}
                            </>
                          )}
                        </TableCell>
                        <TableCell sx={{ color: "#fff" }}>
                          {row.categoryType === "0"
                            ? row.crossFunctionalType
                            : row.categoryType}
                        </TableCell>
                        <TableCell
                          sx={{
                            color: getStatusColor(row.status),
                            fontWeight: 600,
                          }}
                        >
                          {row.statusName}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            onClick={() =>
                              fetchRewardAndRecognitionStatus(row.id)
                            }
                          >
                            <VisibilityIcon sx={{ color: "#fff" }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Grid
              style={{
                justifyContent: "flex-end",
                alignItems: "center",
                display: "flex",
                marginTop: 16,
                padding: 8,
              }}
            >
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                component="div"
                count={totalCount}
                rowsPerPage={pageSize}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setPageSize(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                sx={{
                  color: "#ffffff",
                  "& .MuiSelect-icon": { color: "#ffffff" },
                  "& .MuiSvgIcon-root": { color: "#ffffff" },
                }}
              />
            </Grid>
          </CardContent>
        </Card>
      </Box>
      {/* ------------ Reward acceptance Form Pop Up ------------------- */}
      {rewardAcceptanceFormData && (
        <RewardAcceptanceForm
          open={isRewardAcceptanceFormOpen}
          onClose={() => {
            setIsRewardAcceptanceFormOpen(false);
            navigate("/RandRDashboard");
            fetchCardData();
            fetchTableData();
          }}
          onSave={() => {
            setIsRewardAcceptanceFormOpen(false);
            navigate("/RandRDashboard");
            fetchCardData();
            fetchTableData();
          }}
          rewardAcceptanceFormData={rewardAcceptanceFormData}
        />
      )}
    </>
  );
};

export default RandRDashboard;
